import React from 'react';
import { Button } from './ui/button';
import { ArrowUpRight } from 'lucide-react';
import { hero, heroImage } from '../data/content';
import useCountUp from '../hooks/useCountUp';

export default function Hero() {
  return (
    <section id="top" className="relative pt-32 lg:pt-36 pb-20 lg:pb-28 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-7 relative z-10">
            <p className="sec-num text-sm mb-8 tracking-[0.3em] uppercase mount-up" style={{ '--mount-delay': '80ms' }}>{hero.eyebrow}</p>
            <h1 className="font-serif text-[44px] sm:text-[56px] lg:text-[80px] leading-[0.98] tracking-[-0.02em] text-[#2E2C28] font-light mask-headline is-visible" aria-label="Sales advisory, designed around your stage of growth.">
              <span className="mask-line" style={{ '--reveal-delay': '160ms' }}><span>Sales advisory,</span></span>
              <span className="mask-line" style={{ '--reveal-delay': '260ms' }}><span><em className="italic text-[#B5876C] not-italic" style={{ fontStyle: 'italic' }}>designed</em> around your</span></span>
              <span className="mask-line" style={{ '--reveal-delay': '360ms' }}><span>stage of growth.</span></span>
            </h1>
            <p className="mt-8 text-lg lg:text-xl text-[#57534b] max-w-xl font-light leading-relaxed mount-up" style={{ '--mount-delay': '520ms' }}>
              {hero.sub}
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-6 mount-up" style={{ '--mount-delay': '640ms' }}>
              <Button asChild className="bg-[#2E2C28] hover:bg-[#B5876C] text-[#EBE4DE] rounded-none h-14 px-8 text-[12px] tracking-[0.2em] uppercase font-medium transition-colors duration-500 press">
                <a href="/book" className="inline-flex items-center gap-3">
                  {hero.cta}
                  <ArrowUpRight size={16} strokeWidth={1.5} className="cta-arrow" />
                </a>
              </Button>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] overflow-hidden" data-reveal="image">
              <img src={heroImage} alt="Hannah Hulley, Founder Growth Collective" className="w-full h-full object-cover object-[50%_75%]" width={1200} height={1500} loading="eager" fetchpriority="high" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2E2C28]/20 via-transparent to-transparent" />
            </div>
            <div className="absolute -bottom-6 -left-6 hidden lg:block bg-[#EBE4DE] px-8 py-6 border-l-2 border-[#B5876C] mount-up" style={{ '--mount-delay': '780ms' }}>
              <p className="font-serif italic text-[15px] text-[#57534b] max-w-[220px] leading-snug">Founder-led sales → structured function → scalable revenue.</p>
            </div>
          </div>
        </div>

        {/* stats bar */}
        <div className="mt-24 lg:mt-32 grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 border-t border-b border-[#d9d2ca] py-10">
          <Stat kpi={15} suffix="+" label="Years in B2B sales" delay={0} />
          <Stat kpi="6 → 50" label="SaaS team scaled" delay={80} />
          <Stat kpi="Series C" label="UK expansion led" delay={160} />
          <Stat kpi="Founder-led" label="Specialist advisory" delay={240} />
        </div>
      </div>
    </section>
  );
}

function Stat({ kpi, suffix = '', label, delay = 0 }) {
  const { ref, display } = useCountUp(kpi);
  const isNumber = typeof kpi === 'number';
  return (
    <div data-reveal="up-sm" style={{ '--reveal-delay': `${delay}ms` }}>
      <p ref={ref} className="font-serif text-3xl lg:text-4xl text-[#2E2C28] font-light">{isNumber ? `${Math.round(display)}${suffix}` : kpi}</p>
      <p className="mt-2 text-[11px] tracking-[0.22em] uppercase text-[#7a7369]">{label}</p>
    </div>
  );
}
